import { useCallback } from 'react'
import { useSummaryById, useSummaryDate } from '@renderer/chat/hooks'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useSummaryContext } from './use-summary-context'
import { useDebouncedSave } from './use-debounced-save'

export function useSummaryTaskToggle(
  edited: string | undefined,
  setEdited: (edited: string | undefined) => void,
  onSaveEdits: (updated?: string) => Promise<void>,
) {
  const { summary } = useSummaryContext()
  const { data: fullSummary } = useSummaryById(summary.id)
  const { isToday, dayLabel } = useSummaryDate(summary)
  const { debouncedSave, isSaving } = useDebouncedSave(async (content: string) => {
    await onSaveEdits(content)
  })

  const onClickListItem = useCallback(
    (item: any) => {
      const lineNumber = item?.lineNumber
      if (lineNumber === undefined) return

      const currentMd = edited ?? (fullSummary?.summary || '')
      const lines = currentMd.split('\n')

      // Line numbers from markdown are 1-indexed, convert to 0-indexed array
      const targetLineIndex = lineNumber - 1

      // Validate the line index is within bounds
      if (targetLineIndex < 0 || targetLineIndex >= lines.length) return

      const targetLine = lines[targetLineIndex]

      // Verify this line is actually a task
      if (!/^\s*(?:[*+-]|\d+[.)])?\s*\[[xX ]\]/.test(targetLine)) return

      // Toggle the checkbox: [x] -> [ ] or [ ] -> [x]
      let newCompletedState = false
      const toggledLine = targetLine.replace(/\[([xX ])\]/, (match, char) => {
        const wasChecked = char.trim().toLowerCase() === 'x'
        newCompletedState = !wasChecked
        return `[${wasChecked ? ' ' : 'x'}]`
      })

      if (toggledLine === targetLine) return

      lines[targetLineIndex] = toggledLine
      const newMd = lines.join('\n')

      setEdited(newMd)
      debouncedSave(newMd)

      AmplitudeRenderer.trackEvent('Summary Action', {
        type: 'Task Toggle',
        timePeriod: isToday ? 'Today' : dayLabel,
        isCompleted: newCompletedState,
      })
    },
    [edited, fullSummary?.summary, setEdited, debouncedSave, isToday, dayLabel],
  )

  return { onClickListItem, isSaving }
}
